import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, Edit3, X, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {useTranslation} from '@/lib/i18n/react';
import {NamespaceBoundary} from '@/lib/i18n/NamespaceBoundary';
import {formatNumber} from '@/lib/i18n/runtime';
import {usePersonalIdentity} from '@/features/appearance/session';
import {useOverlayCurrent, useOverlayFocus} from './overlay-lifecycle';

export interface LightboxImage {
  /** Stable identifier for keying — usually the message id */
  id: string;
  url: string;
  alt?: string;
  /** Caption shown in the header (filename, or message text) */
  caption?: string;
  /** Pending local images can expose the pre-send editor from the viewer. */
  editable?: boolean;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
  onEdit?: (image: LightboxImage) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;

/**
 * Full-screen image viewer with zoom, pan, prev/next navigation, keyboard
 * shortcuts, and download. No framer-motion — pure CSS transitions.
 *
 * Keyboard:
 *   Esc       — close
 *   ← / →     — prev / next
 *   + / -     — zoom in / out
 *   0         — reset zoom
 *   double-click — toggle 1× ↔ 2×
 *   wheel     — smooth zoom
 *   drag      — pan when zoomed
 */
export function ImageLightbox(props: ImageLightboxProps) {
  const identity = usePersonalIdentity();
  if (!props.open || !props.images.length) return null;
  return <NamespaceBoundary namespace="image-tools"><OwnedImageLightbox key={identity} {...props}/></NamespaceBoundary>;
}
function OwnedImageLightbox({images, initialIndex = 0, open, onClose, onEdit}: ImageLightboxProps) {
  const {t,locale} = useTranslation('image-tools');
  const rtl = locale === 'ar';
  const currentOwner = useOverlayCurrent();
  const downloadBusy = React.useRef(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loadAttempt, retryLoad] = React.useState(0);
  const [index, setIndex] = React.useState(initialIndex);
  const [scale, setScale] = React.useState(1);
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragStart = React.useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const effectiveIndex = Math.min(Math.max(0,index),images.length-1);
  const current = images[effectiveIndex];
  useOverlayFocus(containerRef, () => {if (currentOwner()) onClose();});
  const hasMany = images.length > 1;

  // The lightbox is used from scroll containers as well as the composer. A
  // document-level lock keeps the page behind it from moving while the
  // viewer owns the viewport.
  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Sync internal index when caller changes initialIndex (e.g. clicked a
  // different image while open).
  React.useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIndex(initialIndex);
      setScale(1);
      setPos({ x: 0, y: 0 });
      setIsLoading(true); setLoadFailed(false);
    }
  }, [open, initialIndex]);

  // Reset zoom when stepping through images
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setScale(1);
    setPos({ x: 0, y: 0 });
    setIsLoading(true); setLoadFailed(false);
  }, [index, loadAttempt]);

  const goPrev = React.useCallback(() => {
    setIndex(effectiveIndex > 0 ? effectiveIndex - 1 : images.length - 1);
  }, [images.length, effectiveIndex]);

  const goNext = React.useCallback(() => {
    setIndex(effectiveIndex < images.length - 1 ? effectiveIndex + 1 : 0);
  }, [images.length, effectiveIndex]);

  const zoomIn = React.useCallback(() => {
    setScale((s) => Math.min(s + SCALE_STEP, MAX_SCALE));
  }, []);

  const zoomOut = React.useCallback(() => {
    setScale((s) => {
      const next = Math.max(s - SCALE_STEP, MIN_SCALE);
      if (next === MIN_SCALE) setPos({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetZoom = React.useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  // Keyboard shortcuts
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          if (hasMany) {e.preventDefault(); (rtl ? goNext : goPrev)();}
          break;
        case 'ArrowRight':
          if (hasMany) {e.preventDefault(); (rtl ? goPrev : goNext)();}
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, hasMany, goPrev, goNext, zoomIn, zoomOut, resetZoom, onClose, rtl]);

  // Wheel zoom — non-passive listener attached imperatively so we can
  // preventDefault and not trigger page scroll behind the modal.
  React.useEffect(() => {
    if (!open) return;
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      setScale((s) => {
        const next = Math.min(Math.max(s + delta, MIN_SCALE), MAX_SCALE);
        if (next === MIN_SCALE) setPos({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open]);

  // Pan when zoomed in
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, posX: pos.x, posY: pos.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || scale <= 1) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPos({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleDoubleClick = () => {
    if (scale > 1) resetZoom();
    else setScale(2);
  };

  const handleDownload = async () => {
    if (!current || !currentOwner() || downloadBusy.current) return;
    const image = current, ordinal = effectiveIndex;
    downloadBusy.current = true; setIsDownloading(true);
    try {
      const res = await fetch(image.url);
      if (!currentOwner()) return;
      if (!res.ok) throw new Error('download_failed');
      const blob = await res.blob();
      if (!currentOwner()) return;
      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a'); a.href = url;
        a.download = image.alt ?? image.caption ?? `image-${ordinal + 1}`;
        document.body.appendChild(a); a.click(); a.remove();
      } finally {window.URL.revokeObjectURL(url);}
    } catch {
      if (currentOwner()) window.open(image.url, '_blank', 'noopener,noreferrer');
    } finally {if (currentOwner()) {downloadBusy.current = false; setIsDownloading(false);}}
  };

  if (!open || !current || typeof document === 'undefined') return null;

  const lightbox = (
    <div
      ref={containerRef} role="dialog" aria-modal="true" aria-label={t('viewer.title')} tabIndex={-1}
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/95 select-none"
      onClick={(e) => {
        if (e.target === containerRef.current) onClose();
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
        <div className="flex items-center gap-3 text-white pointer-events-auto">
          {hasMany && (
            <span className="text-sm font-bold tabular-nums">
              {t('viewer.position',{current:formatNumber(effectiveIndex+1),total:formatNumber(images.length)})}
            </span>
          )}
          {current.caption && (
            <span className="text-xs text-white/70 truncate max-w-md"><bdi>{current.caption}</bdi></span>
          )}
        </div>
        <div className="flex items-center gap-1 pointer-events-auto">
          {scale > 1 && (
            <span className="text-xs font-bold text-white/80 px-2 py-1 bg-white/10 rounded-xs">
              {formatNumber(scale,{style:'percent',maximumFractionDigits:0})}
            </span>
          )}
          {current.editable && onEdit && (
            <HeaderBtn onClick={() => onEdit(current)} title={t('viewer.edit')}>
              <Edit3 size={18} />
            </HeaderBtn>
          )}
          <HeaderBtn disabled={isDownloading} onClick={handleDownload} title={t('viewer.download')}>
            <Download size={18} />
          </HeaderBtn>
          <HeaderBtn onClick={onClose} title={t('viewer.close')}>
            <X size={20} />
          </HeaderBtn>
        </div>
      </div>

      {/* Image */}
      <div className="relative w-full h-full flex items-center justify-center p-12">
        {isLoading && (
          <div role="status" aria-label={t('viewer.loading')} className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader2 size={36} className="text-white/60 animate-spin" />
          </div>
        )}
        <img
          src={current.url}
          key={current.id+':'+loadAttempt} alt={current.alt ?? t('viewer.image')}
          onLoad={() => setIsLoading(false)}
          onError={() => {setIsLoading(false); setLoadFailed(true);}}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          draggable={false}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            opacity: isLoading ? 0 : 1,
          }}
          className={cn(
            'max-w-full max-h-full object-contain',
            scale > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in',
          )}
        />
      </div>

      {loadFailed && <div role="alert" className="absolute inset-x-0 top-24 text-center text-sm text-white"><p>{t('viewer.loadFailed')}</p><button type="button" className="mt-3 underline" onClick={() => retryLoad(n => n+1)}>{t('retry')}</button></div>}

      {/* Prev / next arrows */}
      {hasMany && (
        <>
          <NavBtn onClick={goPrev} side="start" title={t('viewer.previous',{key:rtl?'→':'←'})}>
            <ChevronLeft size={28} className="rtl:rotate-180"/>
          </NavBtn>
          <NavBtn onClick={goNext} side="end" title={t('viewer.next',{key:rtl?'←':'→'})}>
            <ChevronRight size={28} className="rtl:rotate-180"/>
          </NavBtn>
        </>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1.5 rounded-sm bg-black/60 backdrop-blur-sm">
        <ZoomBtn onClick={zoomOut} disabled={scale <= MIN_SCALE} title={t('viewer.zoomOutShortcut')}>
          <ZoomOut size={16} />
        </ZoomBtn>
        <button
          type="button"
          onClick={resetZoom}
          className="px-3 py-1 text-[11px] font-bold text-white/80 hover:text-white hover:bg-white/10 rounded-xs transition-colors min-w-[56px] tabular-nums"
          title={t('viewer.resetShortcut')}
        >
          {formatNumber(scale,{style:'percent',maximumFractionDigits:0})}
        </button>
        <ZoomBtn onClick={zoomIn} disabled={scale >= MAX_SCALE} title={t('viewer.zoomInShortcut')}>
          <ZoomIn size={16} />
        </ZoomBtn>
      </div>

      {/* Thumbnail strip */}
      {hasMany && images.length <= 12 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-2 rounded-sm bg-black/60 backdrop-blur-sm max-w-[90vw] overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={img.id}
              type="button"
              aria-label={t('viewer.thumbnail',{current:formatNumber(i+1),total:formatNumber(images.length)})} aria-pressed={i===effectiveIndex}
              onClick={() => setIndex(i)}
              className={cn(
                'h-12 w-12 rounded-xs overflow-hidden border-2 transition-all flex-shrink-0',
                i === effectiveIndex
                  ? 'border-white scale-110'
                  : 'border-transparent opacity-50 hover:opacity-100',
              )}
            >
              <img src={img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Keep the overlay outside transformed/scrolling composer ancestors. This
  // makes the preview genuinely full-site/full-viewport instead of a panel
  // constrained by the composer layout.
  return createPortal(lightbox, document.body);
}

function HeaderBtn({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      aria-label={props['aria-label'] ?? props.title}
      className="h-9 w-9 flex items-center justify-center rounded-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
    >
      {children}
    </button>
  );
}

function NavBtn({
  side,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { side: 'start' | 'end' }) {
  return (
    <button
      type="button"
      {...props}
      aria-label={props['aria-label'] ?? props.title}
      className={cn(
        'absolute top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/60 hover:bg-black/80 text-white/80 hover:text-white flex items-center justify-center transition-all backdrop-blur-sm z-10',
        side === 'start' ? 'start-4' : 'end-4',
      )}
    >
      {children}
    </button>
  );
}

function ZoomBtn({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      aria-label={props['aria-label'] ?? props.title}
      className="h-7 w-7 flex items-center justify-center rounded-xs text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
